import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { FaAngleLeft, FaAngleRight } from "react-icons/fa";
import "../assets/style/Timetable.css";
import SignUpForLessonModal from "../components/Timetable/SignUpForLessonModal";
import NotificationDialog from "../components/Timetable/SignUpForLessonModal/dialogs/NotificationDialog";
import StoreModal from "../components/StoreModal";
import SingleHour from "../components/Timetable/SingleHour";
import MobileTimetableView from "../components/Timetable/MobileTimetableView";
import { useAuthStore } from "../store";
import LoginModal from "../components/Login";

const TimeTable = () => {
  const [signUpForLessonOpen, setSignUpForLessonOpen] = useState(false);
  const [storeModalOpen, setStoreModalOpen] = useState(false);
  const [values, setValues] = useState({});
  const [lessonData, setLessonData] = useState({});
  const [schedule, setSchedule] = useState([]);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);
  const [weekOffset, setWeekOffset] = useState(0);
  const [scheduleEndpoint, setScheduleEndpoint] = useState(
    `${process.env.REACT_APP_BACKEND_ADDRESS}/lesson_schedule/?mode=hour_by_hour&flat=False&week_offset=${weekOffset}`
  );
  const [weekDate, setWeekDate] = useState({});
  const currentWeekEndpoint = `${process.env.REACT_APP_BACKEND_ADDRESS}/week_date/?week_offset=${weekOffset}`;
  const [isLoginOpen, setIsLoginOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const { isAuthenticated } = useAuthStore((state) => ({
    isAuthenticated: state.isAuthenticated,
  }));

  useEffect(() => {
    axios(scheduleEndpoint)
      .then((response) => {
        setSchedule(response.data);
        setError(null);
      })
      .catch((error) => {
        console.error("Schedule API error:", error.response?.data || error.message);
        setError(error);
      });
  }, [notification, scheduleEndpoint]);

  useEffect(() => {
    axios(currentWeekEndpoint)
      .then((response) => {
        setWeekDate({ ...response.data });
        setScheduleEndpoint(
          `${process.env.REACT_APP_BACKEND_ADDRESS}/lesson_schedule/?mode=hour_by_hour&flat=False&week_offset=${weekOffset}`
        );
      })
      .catch(setError);
  }, [weekOffset, currentWeekEndpoint]);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 20000); // The notification will disappear after 20 seconds
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const handleOpenSignUpModal = (lesson) => {
    // Get the current local time
    const currentTime = new Date();

    // Construct the lesson start time
    const lessonStartTime = new Date(
      lesson.start.year,
      lesson.start.month - 1, // Months are zero-based
      lesson.start.day,
      lesson.start.hour,
      lesson.start.minute,
      0
    );

    // Compare the lesson start time with the current time
    if (lessonStartTime <= currentTime) {
      setNotification({
        message: "Nie można zapisać się na zajęcia w przeszłości.",
        type: "error",
      });
      return;
    }

    // Always open modal - it will handle availability, waitlist, etc.
    if (!isAuthenticated) {
      // User is not authenticated, open login modal
      setIsLoginOpen(true);
      return;
    }

    // User is authenticated, proceed to open sign-up modal
    setLessonData({
      lesson_id: lesson.lesson_id,
      lesson_name: lesson.lesson_type.friendly_name,
      lesson_start: lesson.start,
      lesson_week_day: lesson.week_day,
      trainer: { full_name: lesson.trainer },
      lesson_type: lesson.lesson_type,
      start: lesson.flat_start,
      duration: lesson.lesson_duration,
      is_free: lesson.lesson_type.is_free,
      payment_on_site: lesson.payment_on_site,
      payment_only_online: lesson.payment_only_online,
      lesson_category_id: lesson.lesson_type.lesson_category_id,
      online_payment_amount: lesson.online_payment_amount,
      on_site_payment_amount: lesson.on_site_payment_amount,
    });
    setValues((prevValues) => ({
      ...prevValues,
      lesson_id: lesson.lesson_id,
      lesson_name: lesson.lesson_type.friendly_name,
      lesson_start: lesson.start,
      lesson_week_day: lesson.week_day,
      trainer: { full_name: lesson.trainer },
      lesson_type: lesson.lesson_type,
      start: lesson.flat_start,
      duration: lesson.lesson_duration,
      is_free: lesson.is_free,
      payment_on_site: lesson.payment_on_site,
      payment_only_online: lesson.payment_only_online,
      lesson_category_id: lesson.lesson_type.lesson_category_id,
    }));
    setSignUpForLessonOpen(true);
  };

  if (schedule.length === 0) return <p>Loading</p>;
  if (error) return <p>An error occurred</p>;

  return (
    <section id="timetable" className="d-flex align-items-center">
      <div className="container">
        <div className="section-title">
          <h2>Grafik / Zapisy</h2>
        </div>
        <NotificationDialog
          open={!!notification}
          onClose={() => setNotification(null)}
          notification={notification}
        />

        {isMobile ? (
          <MobileTimetableView
            schedule={schedule}
            handleOpenSignUpModal={handleOpenSignUpModal}
            setValues={setValues}
            values={values}
            setLessonData={setLessonData}
            weekDate={weekDate}
            weekOffset={weekOffset}
            setWeekOffset={setWeekOffset}
          />
        ) : (
          <>
            {/* Desktop Week Navigation */}
            <div className="desktop-week-navigation">
              <button
                className="desktop-week-nav-btn prev-week"
                onClick={() => setWeekOffset(weekOffset - 1)}
                aria-label="Poprzedni tydzień"
              >
                <FaAngleLeft />
                <span className="nav-label">Poprzedni tydzień</span>
              </button>
              <div className="desktop-week-date">
                {weekDate.start} - {weekDate.end}
              </div>
              <button
                className="desktop-week-nav-btn next-week"
                onClick={() => setWeekOffset(weekOffset + 1)}
                aria-label="Następny tydzień"
              >
                <span className="nav-label">Następny tydzień</span>
                <FaAngleRight />
              </button>
            </div>

            {/* Desktop Timetable */}
            <div className="table-responsive">
              <table className="table table-bordered text-center white-table">
                <thead>
                  <tr className="bg-light-gray">
                    <th className="text-uppercase">Poniedziałek</th>
                    <th className="text-uppercase">Wtorek</th>
                    <th className="text-uppercase">Środa</th>
                    <th className="text-uppercase">Czwartek</th>
                    <th className="text-uppercase">Piątek</th>
                    <th className="text-uppercase">Sobota</th>
                    <th className="text-uppercase">Niedziela</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(schedule.objects).map((key) => {
                    let lessons = schedule.objects[key];
                    return (
                      <SingleHour
                        key={key}
                        hour={lessons}
                        handleOpenSignUpModal={handleOpenSignUpModal}
                        setValues={setValues}
                        values={values}
                        setLessonData={setLessonData}
                      />
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
      <SignUpForLessonModal
        open={signUpForLessonOpen}
        onClose={() => setSignUpForLessonOpen(false)}
        setNotification={setNotification}
        lessonData={lessonData}
        setLessonData={setLessonData}
      />
      <StoreModal
        open={storeModalOpen}
        onClose={() => setStoreModalOpen(false)}
      />
      <LoginModal
        open={isLoginOpen}
        handleClose={() => setIsLoginOpen(false)}
        showAlert={(message, type) => {
          setNotification({ message, type });
        }}
      />
    </section>
  );
};

export default TimeTable;
